import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import Page from '../components/Page';
import ConnectedAccountsPanel from '../components/PlatformShares/ConnectedAccountsPanel';
import DeleteShareDialog from '../components/PlatformShares/DeleteShareDialog';
import EditCaptionDialog from '../components/PlatformShares/EditCaptionDialog';
import LivePostsPanel from '../components/PlatformShares/LivePostsPanel';
import ShareHistoryPanel from '../components/PlatformShares/ShareHistoryPanel';
import TokenStatusBanner from '../components/PlatformShares/TokenStatusBanner';
import { PLATFORM_META } from '../components/PlatformShares/shareMeta';
import { baseUrl } from '../constant/config';
import { AuthState } from '../context/auth';

/**
 * Social posts - what is live on the connected Facebook and Instagram accounts.
 *
 * The two platform tabs are read live from the Graph API on every load, so
 * they show whatever is actually on the account - including posts made outside
 * this app. The history tab is our own record, which is the only place a
 * failed publish is ever visible, since Meta has no memory of one.
 *
 * Every Graph call goes through our backend: the Page access token stays on the
 * server and is never handed to the browser.
 */
export default function PlatformShares() {
  // These endpoints are admin-only, and the context attaches the token to axios
  // a tick after mount. Waiting for it avoids firing an unauthenticated request
  // that only succeeds on a retry.
  const { isAuth } = AuthState();

  const [tab, setTab] = useState('facebook');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [toast, setToast] = useState('');

  // Which accounts the server actually has credentials for. Drives the "not
  // connected" chip so an empty tab is never mistaken for an empty account.
  const { data: targets } = useQuery(
    ['shareTargets'],
    async () => {
      const { data } = await axios.get(`${baseUrl}/api/videos/share-targets`);
      return data.data;
    },
    { enabled: Boolean(isAuth), refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000 }
  );

  const tabLabel = (key) => {
    const meta = PLATFORM_META[key];
    const configured = targets?.[key]?.configured;
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        {meta.icon}
        <span>{meta.label}</span>
        {targets && !configured && (
          <Tooltip title={`Server is missing: ${(targets[key].missing || []).join(', ')}`}>
            <Chip size="small" label="not connected" />
          </Tooltip>
        )}
      </Stack>
    );
  };

  const unconfigured = tab !== 'history' && targets && !targets[tab]?.configured;

  // Instagram Business Login is its own consent round trip, so the window is
  // opened only once the server confirms it has the credentials for it.
  const { mutate: connectInstagram, isLoading: startingInstagram } = useMutation(
    async () => {
      const { data } = await axios.get(`${baseUrl}/api/videos/instagram/auth-url`);
      return data.data.url;
    },
    {
      onSuccess: (url) => window.open(url, '_blank', 'noopener'),
      onError: (err) => setToast(err?.response?.data?.message || err.message),
    }
  );

  return (
    <Page title="Social posts">
      <Container>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4">Social posts</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Posts on the connected accounts, read live from the Meta Graph API.
          </Typography>
        </Box>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab value="facebook" label={tabLabel('facebook')} />
          <Tab value="instagram" label={tabLabel('instagram')} />
          <Tab value="history" label="Publish history" />
        </Tabs>

        {unconfigured && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              tab === 'instagram' && (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  disabled={startingInstagram}
                  onClick={() => connectInstagram()}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {startingInstagram ? 'Opening...' : 'Connect Instagram'}
                </Button>
              )
            }
          >
            {PLATFORM_META[tab].label} is not connected
            {tab === 'instagram'
              ? '. Either link an Instagram Business account to the connected Page, or sign in to Instagram directly.'
              : `: ${(targets[tab].missing || []).join(' and ')}.`}
          </Alert>
        )}

        {tab !== 'history' && (
          <>
            {/* Checking a token that was never connected only produces noise -
                the "not connected" notice above already says what to do. */}
            {!unconfigured && <TokenStatusBanner platform={tab} enabled={Boolean(isAuth)} />}
            <ConnectedAccountsPanel enabled={Boolean(isAuth)} />
          </>
        )}

        {tab === 'history' ? (
          <ShareHistoryPanel
            enabled={Boolean(isAuth)}
            onEdit={setEditing}
            onDelete={setDeleting}
            onToast={setToast}
          />
        ) : (
          !unconfigured &&
          isAuth && (
            <LivePostsPanel
              key={tab}
              platform={tab}
              onEdit={setEditing}
              onDelete={setDeleting}
            />
          )
        )}
      </Container>

      <EditCaptionDialog
        open={Boolean(editing)}
        target={editing}
        onClose={() => setEditing(null)}
        onDone={setToast}
      />

      <DeleteShareDialog
        open={Boolean(deleting)}
        target={deleting}
        onClose={() => setDeleting(null)}
        onDone={setToast}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Page>
  );
}
